import {Component, forwardRef, Input} from '@angular/core';
import {SubscribedComponent} from "../../subscribed.component";
import {ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR} from "@angular/forms";
import {User} from "../../interfaces/user";
import {Globals} from "../../shared/Globals";
import moment, {Moment} from "moment";
import {NbDialogService} from "@nebular/theme";
import {PopupDialogComponent} from "../../dialogs/popup-dialog/popup-dialog.component";
import {TranslateService} from "@ngx-translate/core";
import {DatePipe} from "@angular/common";

export interface Capacity{
  id?: number,
  User_id?: number,
  capacity: number,
  capacity_monday: boolean,
  capacity_tuesday: boolean,
  capacity_wednesday: boolean,
  capacity_thursday: boolean,
  capacity_friday: boolean,
  capacity_saturday: boolean,
  capacity_sunday: boolean,
  start_on?: Date|string
}

@Component({
    selector: 'bst-capacity-manager',
    templateUrl: './capacity-manager.component.html',
    styleUrls: ['./capacity-manager.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CapacityManagerComponent),
            multi: true
        }
    ],
    standalone: false
})
export class CapacityManagerComponent extends SubscribedComponent implements ControlValueAccessor{

  @Input() user: User;
  @Input() public disabled:boolean = false;
  @Input() public readonly: boolean = false;

  public capacity: Capacity = CapacityManagerComponent.default();
  public upcoming_capacity: Capacity = null;
  // When the schedule is edited it takes effect from the default date (tomorrow);
  // the effective-date picker stays hidden until the reviewer explicitly chooses to
  // schedule the change for a later date, so the common in-place edit stays clean.
  public scheduleLater: boolean = false;
  private firstValue: Capacity = null;
  public tomorrow: Moment = moment().add(1, 'days');
  public today: Moment = moment();

  // Function to call when the value changes.
  onChange = (value: Capacity) => {};
  // Function to call when the input is touched (when a star is clicked).
  onTouched = () => {};
  public datecontrol: FormControl = new FormControl(this.tomorrow);

  constructor(private dialogService: NbDialogService,
              private translate: TranslateService,
              private datepipe: DatePipe) {
    super();
  }

  public static default(user: User = null){
    return {
      capacity: 40,
      capacity_sunday: false,
      capacity_monday: true,
      capacity_tuesday: true,
      capacity_wednesday: true,
      capacity_thursday: true,
      capacity_friday: true,
      capacity_saturday: false,
      start_on: null,
      User_id: user?.id,
      id: null
    };
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.subs.push(this.datecontrol.valueChanges.subscribe(o => {
      this.capacity.start_on = Globals.toISODate(o);
    }));
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  writeValue(capacities: {capacity: Capacity, upcoming_capacity: Capacity}): void {
    if(!capacities || !capacities.capacity){
      // setting null has no change
      return;
    }

    this.capacity = {...capacities.capacity};
    this.upcoming_capacity = capacities.upcoming_capacity;
    // A fresh value (initial load, or a cancel/save re-patch) is a new editing
    // session, so re-collapse the effective-date picker behind its link.
    this.scheduleLater = false;
    if(!this.firstValue && this.capacity){
      this.firstValue = {...this.capacity};
    }
    this.onChange(this.capacity);
  }

  public get ischanging(){
    if(!this.user){
      return false;
    }

    return this.firstValue.capacity != this.capacity.capacity
    || this.firstValue.capacity_sunday != this.capacity.capacity_sunday
    || this.firstValue.capacity_monday != this.capacity.capacity_monday
    || this.firstValue.capacity_tuesday != this.capacity.capacity_tuesday
    || this.firstValue.capacity_wednesday != this.capacity.capacity_wednesday
    || this.firstValue.capacity_thursday != this.capacity.capacity_thursday
    || this.firstValue.capacity_friday != this.capacity.capacity_friday
    || this.firstValue.capacity_saturday != this.capacity.capacity_saturday;
  }

  textinput($event: Event){
    this.capacity.capacity = parseInt((<HTMLInputElement>$event.target).value);
    this.onChange(this.capacity);
  }

  /**
   * Handles a weekday toggle. Nebular's nbButtonToggle emits `pressedChange`
   * on the initial `[pressed]` binding of each worked (pressed) day — the
   * directive's internal default is `false`, so binding `true` fires the
   * output as an init echo, not a click — which would
   * otherwise run setHours() on edit-mode entry and overwrite the stored
   * capacity. Guard against that by only recalculating when the day's value
   * actually changed — a real user toggle, never the init echo (issue #66).
   */
  setDay(day: 'capacity_sunday' | 'capacity_monday' | 'capacity_tuesday' | 'capacity_wednesday'
       | 'capacity_thursday' | 'capacity_friday' | 'capacity_saturday', pressed: boolean) {
    if (this.capacity[day] === pressed) {
      return;
    }
    this.capacity[day] = pressed;
    this.setHours();
  }

  setHours() {
    if(this.capacity.capacity % Globals.DAY_HOURS === 0){
      let total = 0;
      total += this.capacity.capacity_sunday ? Globals.DAY_HOURS : 0;
      total += this.capacity.capacity_monday ? Globals.DAY_HOURS : 0;
      total += this.capacity.capacity_tuesday ? Globals.DAY_HOURS : 0;
      total += this.capacity.capacity_wednesday ? Globals.DAY_HOURS : 0;
      total += this.capacity.capacity_thursday ? Globals.DAY_HOURS : 0;
      total += this.capacity.capacity_friday ? Globals.DAY_HOURS : 0;
      total += this.capacity.capacity_saturday ? Globals.DAY_HOURS : 0;

      this.capacity.capacity = total;
    }
    this.onChange(this.capacity);
  }

  selectDate($event: Moment) {
    this.capacity.start_on = Globals.toISODate($event);
  }

  openUpcoming() {
    const title = this.translate.instant('changing', {
      value: (this.datepipe.transform(this.upcoming_capacity.start_on, 'longDate'))
    });
    this.dialogService.open<PopupDialogComponent<CapacityManagerComponent>>(PopupDialogComponent, {
      context: {
        title: title,
        comp: CapacityManagerComponent,
        componentContext: {
          readonly: true,
          capacity: this.upcoming_capacity
        }
      }
    });
  }
}
